import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import {
  anchoredPosition,
  clampPosition,
  computeFitScale,
  resolveWheelIntent,
  roundScale,
  SCALE_STEP,
  TFitMode,
  TPosition,
  TViewport,
} from "@/features/talent/components/talent-scan-image/zoom-pan-geometry";

type TUseZoomPanParams = {
  contentWidth: number;
  contentHeight: number;
  containerRef: RefObject<HTMLDivElement>;
  fitOnLoad?: boolean;
  maxFitScale?: number;
  fitMode?: TFitMode;
  enabled?: boolean;
};

const DOUBLE_CLICK_ZOOM = 2;

export const useZoomPan = ({
  contentWidth,
  contentHeight,
  containerRef,
  fitOnLoad = false,
  maxFitScale,
  fitMode = "contain",
  enabled = true,
}: TUseZoomPanParams) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState<TPosition>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const dragStartRef = useRef<TPosition>({ x: 0, y: 0 });
  // Latest scale/position, so the native wheel listener need not re-bind on every change.
  const stateRef = useRef({ scale, position });
  stateRef.current = { scale, position };

  const getViewport = useCallback((): TViewport | null => {
    const container = containerRef.current;
    if (!container) return null;
    return { width: container.clientWidth, height: container.clientHeight };
  }, [containerRef]);

  const getFitScale = useCallback(() => {
    const viewport = getViewport();
    if (!viewport) return 1;
    return computeFitScale(contentWidth, contentHeight, viewport, maxFitScale ?? Number.POSITIVE_INFINITY, fitMode);
  }, [getViewport, contentWidth, contentHeight, maxFitScale, fitMode]);

  /** Applies a new scale, keeping `anchor` (viewport coords) over the same content point. */
  const applyScale = useCallback(
    (nextScale: number, anchor?: TPosition) => {
      const viewport = getViewport();
      if (!viewport || contentWidth <= 0) return;

      const { scale: currentScale, position: currentPosition } = stateRef.current;
      // Round first: position must derive from the scale actually rendered.
      const newScale = roundScale(nextScale);
      if (newScale === currentScale) return;

      const focus = anchor ?? { x: viewport.width / 2, y: viewport.height / 2 };
      const nextPosition = anchoredPosition(focus, currentPosition, currentScale, newScale);

      setScale(newScale);
      setPosition(clampPosition(nextPosition, contentWidth * newScale, contentHeight * newScale, viewport));
    },
    [getViewport, contentWidth, contentHeight],
  );

  // Centre content when its size settles. `scale` is deliberately absent from the deps:
  // re-centring on every scale change would fight zoom-to-cursor. Re-running when the
  // dimensions themselves change is intended — that is a new file being previewed.
  useEffect(() => {
    const viewport = getViewport();
    if (!viewport || contentWidth <= 0 || contentHeight <= 0) return;

    const initialScale = fitOnLoad ? getFitScale() : 1;
    setScale(initialScale);
    setPosition(clampPosition({ x: 0, y: 0 }, contentWidth * initialScale, contentHeight * initialScale, viewport));
  }, [contentWidth, contentHeight, fitOnLoad, getFitScale, getViewport]);

  const zoomIn = useCallback(() => applyScale(stateRef.current.scale + SCALE_STEP), [applyScale]);
  const zoomOut = useCallback(() => applyScale(stateRef.current.scale - SCALE_STEP), [applyScale]);
  const resetTo100 = useCallback(() => applyScale(1), [applyScale]);
  const fitToViewport = useCallback(() => applyScale(getFitScale()), [applyScale, getFitScale]);

  const panBy = useCallback(
    (deltaX: number, deltaY: number) => {
      const viewport = getViewport();
      if (!viewport) return;

      const { scale: currentScale, position: currentPosition } = stateRef.current;
      setPosition(
        clampPosition(
          { x: currentPosition.x - deltaX, y: currentPosition.y - deltaY },
          contentWidth * currentScale,
          contentHeight * currentScale,
          viewport,
        ),
      );
    },
    [getViewport, contentWidth, contentHeight],
  );

  // Bound natively: React attaches wheel listeners passively, which rejects preventDefault.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const zoomAt = (event: WheelEvent) => {
      const rect = container.getBoundingClientRect();
      const delta = event.deltaY > 0 ? -SCALE_STEP : SCALE_STEP;
      applyScale(stateRef.current.scale + delta, { x: event.clientX - rect.left, y: event.clientY - rect.top });
    };

    const handleWheel = (event: WheelEvent) => {
      const viewport = getViewport();
      if (!viewport) return;

      const { scale: currentScale } = stateRef.current;
      const intent = resolveWheelIntent(event, contentWidth * currentScale, contentHeight * currentScale, viewport);
      if (intent === "ignore") return;

      event.preventDefault();
      if (intent === "zoom") {
        event.stopPropagation();
        zoomAt(event);
        return;
      }
      // Browsers may remap the scroll amount onto deltaX while a modifier is held,
      // so take whichever axis actually carries it.
      const amount = event.deltaY !== 0 ? event.deltaY : event.deltaX;
      if (intent === "pan-horizontal") {
        panBy(amount, 0);
        return;
      }
      panBy(0, amount);
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [containerRef, enabled, applyScale, panBy, getViewport, contentWidth, contentHeight]);

  const onMouseDown = useCallback((event: React.MouseEvent) => {
    setIsDragging(true);
    const { position: current } = stateRef.current;
    dragStartRef.current = { x: event.clientX - current.x, y: event.clientY - current.y };
  }, []);

  const onMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!isDragging) return;
      const viewport = getViewport();
      if (!viewport) return;

      const { scale: currentScale } = stateRef.current;
      setPosition(
        clampPosition(
          { x: event.clientX - dragStartRef.current.x, y: event.clientY - dragStartRef.current.y },
          contentWidth * currentScale,
          contentHeight * currentScale,
          viewport,
        ),
      );
    },
    [isDragging, getViewport, contentWidth, contentHeight],
  );

  const onMouseUp = useCallback(() => setIsDragging(false), []);

  const onDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      applyScale(stateRef.current.scale > 1 ? 1 : DOUBLE_CLICK_ZOOM, anchor);
    },
    [containerRef, applyScale],
  );

  return {
    scale,
    position,
    isDragging,
    zoomIn,
    zoomOut,
    resetTo100,
    fitToViewport,
    handlers: { onMouseDown, onMouseMove, onMouseUp, onMouseLeave: onMouseUp, onDoubleClick },
  };
};
